import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const STORE_API = `${BACKEND}/api/store`;
export const imgUrl = (u) => (!u ? "/brand/paw.png" : u.startsWith("/api") ? `${BACKEND}${u}` : u);
export const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
let brandsPromise = null;
export function useStoreBrands() {
  const [brands, setBrands] = useState(null);
  useEffect(() => {
    brandsPromise = brandsPromise || axios.get(`${STORE_API}/products`, { params: { per_page: 1 } }).then((r) => r.data.brands).catch(() => []);
    brandsPromise.then(setBrands);
  }, []);
  return brands;
}
export const pctOff = (price, mrp) => (mrp > price ? Math.round((1 - price / mrp) * 100) : 0);

const CartCtx = createContext(null);
const KEY = "bf_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({ shipping_fee: 79, free_shipping_above: 999, payments_enabled: null });

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);
  useEffect(() => { axios.get(`${STORE_API}/settings`).then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const add = useCallback((product, variant, qty = 1, openDrawer = true) => {
    setItems((list) => {
      const key = `${product.id}:${variant.id}`;
      const found = list.find((i) => i.key === key);
      const max = variant.stock ?? 50;
      if (found) return list.map((i) => (i.key === key ? { ...i, qty: Math.min(max, i.qty + qty) } : i));
      return [...list, {
        key, product_id: product.id, variant_id: variant.id, slug: product.slug, name: product.name,
        brand: product.brand, variant_label: variant.label, price: variant.price, mrp: variant.mrp,
        image: product.images?.[0], qty: Math.min(max, qty), max,
      }];
    });
    if (openDrawer) setOpen(true);
    window.gtag?.("event", "add_to_cart", { currency: "INR", value: variant.price * qty, items: [{ item_id: product.id, item_name: product.name, item_variant: variant.label }] });
  }, []);

  const setQty = useCallback((key, qty) => setItems((l) => l.map((i) => (i.key === key ? { ...i, qty: Math.max(1, Math.min(i.max || 50, qty)) } : i))), []);
  const remove = useCallback((key) => setItems((l) => l.filter((i) => i.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
    const mrpTotal = items.reduce((s, i) => s + i.mrp * i.qty, 0);
    const shipping = !items.length || subtotal >= settings.free_shipping_above ? 0 : settings.shipping_fee;
    return {
      items, open, setOpen, add, setQty, remove, clear, settings,
      count: items.reduce((s, i) => s + i.qty, 0), subtotal, mrpTotal, shipping, total: subtotal + shipping,
    };
  }, [items, open, add, setQty, remove, clear, settings]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
